import { makeStyles, tokens } from '@fluentui/react-components';
import { customPalette } from '../theme';

export const useAccountAmountStyles = makeStyles({
    customTokens: {
        '--list-header-height': '26px', // 26px since we're using content-box sizing. if we were using border-box, this would be 28
        '--editvaluebutton-column-width': '42px',
        '--reportamount-column-width': '106px',
        '--dynamic-width': 'calc(100% - var(--editvaluebutton-column-width) - var(--reportamount-column-width))',

        '--account-column-width': '148px',
        '--adjustmentCode-column-width': '148px',
        '--type-column-width': '56px',
        '--description-column-width': '346px',

        '--description-column-width-withAdjustment':
            'calc(var(--description-column-width) - calc(var(--editvaluebutton-column-width) * 3))',
        // We need to reference this border color through the palette for now because the UX
        // team has not figured out which alias token to use for this color.
        '--listcard-border-color': customPalette.blueGreyNeutral,
        '--listcard-border-radius': '6px',
        '--listcard-box-shadow-margin': '6px',
        '--listcard-box-shadow': '0px 0.3px 0.9px 0px rgba(0, 0, 0, 0.10), 0px 1.6px 3.6px 0px rgba(0, 0, 0, 0.13)',
        '--listcard-header-border-color': customPalette.statusCoolDarkGrey,
    },
    treeHeader: {
        fontSize: tokens.fontSizeBase200,
        fontWeight: tokens.fontWeightSemibold,
        lineHeight: '16px',
        alignContent: 'center',
        padding: '0px 8px',
    },
    treeRoot: {
        rowGap: '10px',
        overflowY: 'auto',
        scrollbarGutter: 'stable',
        width: 'calc(100% + 0.656vw)',
        height: 'calc(98% - var(--list-header-height))',
    },
    treeNoGap: {
        paddingTop: '0px',
        rowGap: '0px',
    },
    treeChevron: {
        alignSelf: 'center',
    },
    flexFullWidth: {
        display: 'flex',
        width: '100%',
        justifyContent: 'space-between',
    },
    alignItemsStart: {
        alignItems: 'start',
    },
    alignItemsCenter: {
        alignItems: 'center',
    },
    summaryRow: {
        backgroundColor: tokens.colorNeutralBackground1,
        ':hover': {
            backgroundColor: tokens.colorNeutralBackground1,
        },
        // this is the min-width of the expand/collapse icon
        // this needs to be specified or else the group header won't align with the summary row
        paddingLeft: tokens.spacingHorizontalXXL,
    },
    treeRow: {
        borderBottomColor: tokens.colorNeutralStroke1,
        borderBottomStyle: 'solid',
        borderBottomWidth: '1px',
    },
    treeRowLast: {
        borderBottomWidth: '0px',
    },
    bookEntryRow: {
        backgroundColor: customPalette.blueGreyNeutralTint2,
        ':hover': {
            backgroundColor: customPalette.blueGreyNeutralTint2,
        },
    },
    adjustmentEntryRow: {
        backgroundColor: customPalette.statusCoolMidGrey,
        ':hover': {
            backgroundColor: customPalette.statusCoolMidGrey,
        },
    },
    border: {
        border: `1px solid var(--listcard-border-color)`,
        borderRadius: 'var(--listcard-border-radius)',
        overflow: 'hidden',
        minHeight: 'fit-content',
        boxShadow: 'var(--listcard-box-shadow)',
    },
    groupHeader: {
        backgroundColor: customPalette.blueC7DCE5,
        ':hover': {
            backgroundColor: customPalette.blueC7DCE5,
        },
    },
    accountColumnHeader: {
        flex: 'var(--account-column-width) 1 1',
        padding: '0px 8px',
        textWrapMode: 'nowrap',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    accountColumn: {
        flex: `calc(var(--account-column-width) - ${tokens.spacingHorizontalXXL}) 1 1`,
        height: 'var(--list-header-height)',
        textWrapMode: 'nowrap',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    reportAmountColumn: {
        textAlign: 'right',
        flex: 'var(--reportamount-column-width) 0 0',
        padding: '0 10px',
        height: 'var(--list-header-height)',
        textWrapMode: 'nowrap',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    descriptionColumn: {
        display: 'flex',
        alignItems: 'center',
        width: 'var(--description-column-width)',
        flex: 'var(--description-column-width) 1 1',
        padding: '0 10px',
        height: 'var(--list-header-height)',
        textWrapMode: 'nowrap',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    descriptionColumnSmall: {
        display: 'flex',
        alignItems: 'center',
        flex: 'var(--description-column-width-withAdjustment) 1 1',
        padding: '0px 8px',
        height: 'var(--list-header-height)',
        textWrapMode: 'nowrap',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    adjustmentTypeColumn: {
        flex: 'var(--type-column-width) 1 0',
        height: 'var(--list-header-height)',
        padding: '0px 8px',
        textWrapMode: 'nowrap',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    caseColumn: {
        flex: '1 1 20%',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        textWrapMode: 'nowrap',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    entityColumn: {
        flex: '1 1 30%',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        textWrapMode: 'nowrap',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    jurisdictionColumn: {
        flex: '1 1 30%',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        textWrapMode: 'nowrap',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    periodColumn: {
        flex: '1 1 20%',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        textWrapMode: 'nowrap',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    cellText: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        textWrapMode: 'nowrap',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    lastModifiedByColumn: {
        flex: 'var(--editvaluebutton-column-width) 0 0',
        padding: '0 10px',
        height: 'var(--list-header-height)',
        textWrapMode: 'nowrap',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    changedBackground: {
        backgroundColor: customPalette.warningBackground,
    },
    adjustmentsHeader: {
        backgroundColor: customPalette.statusCoolDarkGrey,
        ':hover': {
            backgroundColor: customPalette.statusCoolDarkGrey,
        },
        borderBottomColor: customPalette.cementGrey,
        borderBottomStyle: 'solid',
        borderBottomWidth: '1px',
        height: 'var(--list-header-height)',
    },
    enterAmountsButtonHeader: {
        flexBasis: 'var(--editvaluebutton-column-width)',
        flexGrow: '0',
        flexShrink: '0',
        padding: '0px 8px',
        textWrapMode: 'nowrap',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    journalEntryButtonHeader: {
        flexBasis: 'var(--editvaluebutton-column-width)',
        flexGrow: '0',
        flexShrink: '0',
        padding: '0px 8px',
        textWrapMode: 'nowrap',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    alignContentCenter: {
        alignContent: 'center',
    },
    infoIcon: {
        verticalAlign: 'middle',
    },
    disabled: {
        backgroundColor: tokens.colorNeutralBackgroundDisabled,
    },
    editableBackground: {
        backgroundColor: tokens.colorNeutralBackground1,
    },
    noRecordsDisplay: {
        margin: '5px',
        padding: '7px 7px 7px 30px',
    },
    rightMargin: {
        marginRight: 'var(--listcard-box-shadow-margin)',
        marginLeft: 'var(--listcard-box-shadow-margin)',
    },
    topMargin: {
        marginTop: 'var(--listcard-box-shadow-margin)',
    },
    fontWeightSemibold: {
        fontWeight: tokens.fontWeightSemibold,
    },
    fontWeightBold: {
        fontWeight: tokens.fontWeightBold,
    },
    fontSize: {
        fontSize: tokens.fontSizeBase200,
    },
    treeItemLayoutMain: {
        padding: tokens.spacingHorizontalNone,
    },
    headerRowContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        border: '1px solid var(--listcard-header-border-color)',
        marginLeft: '6px',
        marginRight: '4px',
    },
    loadingSpinnerContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxSizing: 'content-box',
        height: '100%',
        width: '100%',
    },
});
